/**
 * Represents the type of data being displayed in the DataGrid
 *
 * @enum DataType
 * @property {string} Negative - Negative scores.
 * @property {string} Positive - Positive scores.
 * @property {string} Correlations - Correlations.
 */
export enum DataType {
  Negative = "negative",
  Positive = "positive",
  Correlations = "correlations",
}

/**
 * Represents the type of column in the DataGrid and helps pick rendering functions accordingly.
 *
 * @enum ColumnType
 * @property {string} Library - Library column.
 * @property {string} Query - Query column.
 * @property {string} Correlation - Correlation column.
 */
export enum ColumnType {
  Library = "library",
  Query = "query",
  Correlation = "correlation",
}

/**
 * Enum representing HTTP request types.
 *
 * @enum RequestType
 * @property {string} Post - POST request.
 * @property {string} Get - GET request.
 */
export enum RequestType {
  Post = "POST",
  Get = "GET"
}

/**
 * Enum representing the different types of figures that can be used in scatter plot visualizations.
 *
 * @enum FigureType
 * @property {string} ScoreAll - Scatter plot for all scores.
 * @property {string} ScoreQuery - Scatter plot for query scores.
 * @property {string} ScoreLibrary - Scatter plot for library scores.
 * @property {string} CorrelationDecomp - Scatter plot for correlation decomposition.
 * @property {string} CorrelationSubnetworkG1 - Scatter plot for subnetwork G1 correlation.
 * @property {string} CorrelationSubnetworkG2 - Scatter plot for subnetwork G2 correlation.
 */
export enum FigureType {
  ScoreAll = "scatter_scores",
  ScoreQuery = "scatter_scores_query",
  ScoreLibrary = "scatter_scores_library",
  CorrelationDecomp = "scatter_correlations_decomp",
  CorrelationSubnetworkG1 = "scatter_correlations_subnetwork",
  // FIXME: This is a hack to get the subnetwork G2 scatter plot to work, points to above in backend. My prediction is
  //        that this will stay like this forever.
  CorrelationSubnetworkG2 = "scatter_correlations_subnetwork_g2",
}

/**
 * Represents the type of detail view modal to open for a score or correlation row.
 *
 * @enum DetailType
 * @property {string} QGI - Detail for qGI.
 * @property {string} PCC - Detail for PCC.
 */
export enum DetailType {
  QGI = 'detail_qgi',
  PCC = 'detail_pcc',
}

/**
 * Represents the API endpoint for fetching modal data
 *
 * @enum RowModalType
 * @property {string} Library - Library API call.
 * @property {string} Query - Query API call.
 */
export enum RowModalType {
  Library = 'library_info',
  Query = 'query_info',
}

/**
 * Represents the type of query to be used for qGI scores data fetching
 *
 * @enum QGiQueryType
 * @property {string} All - All data.
 * @property {string} Query - Query data.
 * @property {string} Screen - Screen data.
 * @property {string} Library - Library data.
 */
export enum QGiQueryType {
  All = 'all',
  Query = 'query',
  Screen = 'screen',
  Library = 'library',
}

/**
 * Represents the types of alerts that can be used in the application.
 * This enumeration provides a predefined set of alert types to standardize
 * messaging and categorization across the system.
 *
 * @enum AlertType
 * @property {string} Error - Represents an error alert.
 * @property {string} Warning - Represents a warning alert.
 * @property {string} Info - Represents an informational alert.
 */
export enum AlertType {
  Error = 'danger',
  Warning = 'warning',
  Info = 'info',
}

/**
 * Represents which enrichment to run and display in overlay
 *
 * @enum OverlayType
 * @property {string} SAFE - Safe overlay type.
 * @property {string} RISK - Risky overlay type.
 */
export enum OverlayType {
  SAFE = "safe",
  RISK = "risk",
}

/**
 * Represents the type of data to submit for enrichment analysis
 *
 * @enum OverlayDataType
 * @property {string} SCREEN - Screen data type.
 * @property {string} CUSTOM - Custom data type.
 */
export enum OverlayDataType {
  SCREEN = "screen",
  CUSTOM = "custom"
}

/**
 * Represents the URL parameters used in the application.
 *
 * @enum URLParam
 * @property {string} GENES - Parameter for selected genes list.
 * @property {string} LIST_FOCUS - Parameter for current gene in list view.
 * @property {string} LIST_TYPE - Parameter for selected data type in list view.
 */
export enum URLParam {
  GENES = "g",
  LIST_FOCUS = "c",
  LIST_TYPE = "t",
}